import { IWFStep } from "@kuika/kuika-cl-model";
import { useMemo, useState } from "react";
import { useDebouncedValue } from "../../../../shared/hooks/useDebouncedValue";
import {
  IStepScreenStateIcon,
  STEP_SCREEN_STATE_ICON,
  StepScreenState
} from "../../../common/step-screen-form-wizard/step-screen-state-icon";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";

export type { StepScreenState };

/** How a step's bound screen reads in the list: its name, or the reason it has none. */
export interface IStepScreenLabel {
  /** Sub-title text under the step name (screen name or a state like "No Screen"). */
  text: string;
  /** Drives the leading status icon and its colour. */
  state: StepScreenState;
}

export interface IFormsTabSidebarProps {
  steps: IWFStep[];
  selectedStepId: string | null;
  onSelect: (stepId: string) => void;
  /** Resolves the screen sub-label for a step (kept in the shell where the sd context lives). */
  resolveScreenLabel: (step: IWFStep) => IStepScreenLabel;
}

/**
 * Left rail of the Forms tab: the current process's user tasks, one row each. Purely
 * presentational — the shell owns selection and screen resolution.
 */
const FormsTabSidebar = ({ steps, selectedStepId, onSelect, resolveScreenLabel }: IFormsTabSidebarProps) => {
  const [search, setSearch] = useState<string>("");
  // The input echoes `search` per keystroke; the list re-filters only once typing pauses.
  const filterQuery: string = useDebouncedValue(search);

  const filtered: IWFStep[] = useMemo((): IWFStep[] => {
    const needle: string = filterQuery.trim().toLowerCase();
    if (!needle) return steps;
    return steps.filter((step: IWFStep): boolean => (step.name ?? "").toLowerCase().includes(needle));
  }, [steps, filterQuery]);

  return (
    <div className="kuika-wf-forms__sidebar" data-testid="processFormsSidebar">
      <div className="kuika-wf-forms__sidebar-header">
        <span className="kuika-wf-forms__sidebar-title">USER TASKS</span>
        <span className="kuika-wf-forms__sidebar-count" data-testid="processFormsSidebarCount">
          {steps.length}
        </span>
      </div>
      <KuikaInput
        className="kuika-wf-forms__sidebar-search"
        size="md"
        placeholder="Search steps..."
        value={search}
        leftIcon={<KuikaSvg name="search" width={14} height={14} />}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void => setSearch(String(value))}
        data-testid="processFormsSidebarSearch"
      />
      <div className="kuika-wf-forms__sidebar-list">
        {filtered.map((step: IWFStep) => {
          const label: IStepScreenLabel = resolveScreenLabel(step);
          const icon: IStepScreenStateIcon = STEP_SCREEN_STATE_ICON[label.state];
          const isSelected: boolean = step.id === selectedStepId;
          return (
            <button
              key={step.id}
              type="button"
              className={`kuika-wf-forms__task${isSelected ? " kuika-wf-forms__task--active" : ""}`}
              onClick={(): void => onSelect(step.id)}
              data-testid={`processFormsTask-${step.id}`}
            >
              <span className="kuika-wf-forms__task-bullet" />
              <span className="kuika-wf-forms__task-text">
                <span className="kuika-wf-forms__task-name" data-testid="processFormsTaskName">
                  {step.name || "Untitled step"}
                </span>
                <span className="kuika-wf-forms__task-sub">{label.text}</span>
              </span>
              <span className="kuika-wf-forms__task-icon" title={icon.title}>
                <KuikaSvg name={icon.name} width={16} height={16} color={icon.color} />
              </span>
            </button>
          );
        })}
        {filtered.length === 0 && <div className="kuika-wf-forms__sidebar-empty">No user tasks match your search.</div>}
      </div>
    </div>
  );
};

export default FormsTabSidebar;
